// src/pages/AdminCourses.js
import { useState, useEffect } from 'react';
import { 
  getAllCourses, 
  updateCourseStatus, 
  deleteCourse,
} from '../../../services/adminService';
import './AdminCourses.css';

function AdminCourses() {
  const [courses, setCourses] = useState([]);
  const [stats, setStats] = useState({
    totalCourses: 0,
    activeCourses: 0,
    totalStudents: 0,
    totalLessons: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [viewMode, setViewMode] = useState('all'); // 'all', 'active', 'inactive'

  useEffect(() => {
    loadCoursesData();
  }, []);

  const loadCoursesData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const coursesData = await getAllCourses();
      
      setCourses(coursesData);
      calculateStats(coursesData);
      
    } catch (err) {
      const errorMessage = err.response?.data?.msg || err.message || 'Kurslar yüklenirken bir hata oluştu';
      setError(errorMessage);
      console.error('Kurs yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (coursesData) => {
    const totalCourses = coursesData.length;
    const activeCourses = coursesData.filter(course => 
      course.status === 'active'
    ).length;
    const totalStudents = coursesData.reduce((acc, course) => 
      acc + (course.enrolledStudents || 0), 0
    );
    const totalLessons = coursesData.reduce((acc, course) => 
      acc + (course.lessonCount || 0), 0
    );

    setStats({
      totalCourses,
      activeCourses,
      totalStudents,
      totalLessons
    });
  };

  const handleStatusUpdate = async (courseId, newStatus) => {
    try {
      setActionLoading(true);
      
      await updateCourseStatus(courseId, newStatus);
      
      // Local state'i güncelle
      const updatedCourses = courses.map(course => 
        course._id === courseId 
          ? { ...course, status: newStatus }
          : course
      );
      setCourses(updatedCourses);
      
      // İstatistikleri yeniden hesapla
      calculateStats(updatedCourses);
      
    } catch (err) {
      const errorMessage = err.response?.data?.msg || err.message || 'Kurs durumu güncellenirken hata oluştu';
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm('Bu kursu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
      return;
    }

    try {
      setActionLoading(true);
      
      await deleteCourse(courseId);
      
      // Local state'ten kaldır
      const updatedCourses = courses.filter(course => course._id !== courseId);
      setCourses(updatedCourses);
      
      // İstatistikleri yeniden hesapla
      calculateStats(updatedCourses);
      
    } catch (err) {
      const errorMessage = err.response?.data?.msg || err.message || 'Kurs silinirken hata oluştu';
      setError(errorMessage);
    } finally {
      setActionLoading(false);
    }
  };

  const filteredCourses = courses.filter(course => {
    if (viewMode === 'all') return true;
    if (viewMode === 'active') return course.status === 'active';
    if (viewMode === 'inactive') return course.status === 'inactive' || course.status === 'draft';
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'Bilinmiyor';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getStatusText = (status) => {
    const statusMap = {
      'active': 'Published',
      'inactive': 'Inactive',
      'draft': 'Draft'
    };
    return statusMap[status] || status;
  };

  const getStatusClass = (status) => {
    const classMap = {
      'active': 'active',
      'inactive': 'inactive',
      'draft': 'draft'
    };
    return classMap[status] || 'draft';
  };

  if (loading) {
    return (
      <div className="admin-courses-loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
        <p>Loading courses...</p>
      </div>
    );
  }

  if (error && courses.length === 0) {
    return (
      <div className="admin-courses-error">
        <div className="error-icon">⚠️</div>
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={loadCoursesData} className="retry-btn">
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="admin-courses">
      {/* Header */}
      <div className="courses-header">
        <div className="header-content">
          <h1>📚 Course Management</h1>
          <p>View and manage all courses in the system</p>
        </div>
        <button 
          className="add-course-btn"
          onClick={() => alert('Add course modal will be added soon')}
          disabled={actionLoading}
        >
          {actionLoading ? '⏳' : '➕'} Add New Course
        </button>
      </div>

      {error && (
        <div className="courses-error-banner">
          <span>⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError('')} className="close-btn">×</button>
        </div>
      )}

      {/* Filter Buttons */}
      <div className="courses-filters">
        <button 
          className={`filter-btn ${viewMode === 'all' ? 'active' : ''}`}
          onClick={() => setViewMode('all')}
        >
          All ({courses.length})
        </button>
        <button 
          className={`filter-btn ${viewMode === 'active' ? 'active' : ''}`}
          onClick={() => setViewMode('active')}
        >
          Active ({courses.filter(c => c.status === 'active').length})
        </button>
        <button 
          className={`filter-btn ${viewMode === 'inactive' ? 'active' : ''}`}
          onClick={() => setViewMode('inactive')}
        >
          Inactive ({courses.filter(c => c.status === 'inactive' || c.status === 'draft').length})
        </button>
      </div>

      {/* Statistics */}
      <div className="courses-stats">
        <div className="stat-card">
          <div className="stat-icon">📚</div>
          <div className="stat-info">
            <span className="stat-value">{stats.totalCourses}</span>
            <span className="stat-label">Total Courses</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">👥</div>
          <div className="stat-info">
            <span className="stat-value">{stats.totalStudents}</span>
            <span className="stat-label">Total Students</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <span className="stat-value">{stats.activeCourses}</span>
            <span className="stat-label">Active Courses</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">📖</div>
          <div className="stat-info">
            <span className="stat-value">{stats.totalLessons}</span>
            <span className="stat-label">Total Lessons</span>
          </div>
        </div>
      </div>

      {/* Course Grid */}
      <div className="courses-grid">
        {filteredCourses.length === 0 ? (
          <div className="no-courses">
            <div className="no-courses-icon">📚</div>
            <h3>No courses yet</h3>
            <p>Click "Add New Course" to create your first course.</p>
          </div>
        ) : (
          filteredCourses.map(course => (
            <div key={course._id} className="course-card">
              {course.imageUrl && (
                <div className="course-image">
                  <img src={course.imageUrl} alt={course.title} />
                </div>
              )}
              
              <div className="course-header">
                <h3>{course.title}</h3>
                <span className={`status-badge ${getStatusClass(course.status)}`}>
                  {getStatusText(course.status)}
                </span>
              </div>
              
              <div className="course-meta">
                <span className="meta-item">📁 {course.category || 'No Category'}</span>
                <span className="meta-item">👨‍🏫 {course.instructor?.name || 'No Instructor'}</span>
              </div>

              {course.description && (
                <div className="course-description">
                  {course.description.length > 100 
                    ? `${course.description.substring(0, 100)}...` 
                    : course.description
                  }
                </div>
              )}

              <div className="course-stats">
                <div className="stat-item">
                  <span>👥 Students</span>
                  <span>{course.enrolledStudents}</span>
                </div>
                <div className="stat-item">
                  <span>📖 Lessons</span>
                  <span>{course.lessonCount}</span>
                </div>
                <div className="stat-item">
                  <span>📅 Created</span>
                  <span>{formatDate(course.createdAt)}</span>
                </div>
              </div>

              <div className="course-actions">
                <button 
                  className="btn-edit"
                  disabled={actionLoading}
                  onClick={() => alert('Edit modal will be added soon')}
                >
                  ✏️ Edit
                </button>
                <button 
                  className="btn-view"
                  disabled={actionLoading}
                  onClick={() => window.open(`/courses/${course._id}`, '_blank')}
                >
                  👁️ View
                </button>
                {course.status === 'active' ? (
                  <button 
                    className="btn-deactivate"
                    onClick={() => handleStatusUpdate(course._id, 'inactive')}
                    disabled={actionLoading}
                  >
                    {actionLoading ? '⏳' : '❌'} Deactivate
                  </button>
                ) : (
                  <button 
                    className="btn-activate"
                    onClick={() => handleStatusUpdate(course._id, 'active')}
                    disabled={actionLoading}
                  >
                    {actionLoading ? '⏳' : '✅'} Activate
                  </button>
                )}
                <button 
                  className="btn-delete"
                  onClick={() => handleDeleteCourse(course._id)}
                  disabled={actionLoading}
                >
                  🗑️ Delete
                </button>
              </div>

              <div className="course-footer">
                <span>Last updated: {formatDate(course.updatedAt || course.createdAt)}</span>
                {course.zoomLink && (
                  <a href={course.zoomLink} target="_blank" rel="noopener noreferrer" className="zoom-link">
                    🔗 Zoom Link
                  </a>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminCourses;